import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface ProductFilters {
  categories: number[];
  locations: string[];
  priceRange: { min: number; max: number };
  brands: string[];
  conditions: string[];
  productTypes: string[];
}

interface ProductFilterContextType {
  filters: ProductFilters;
  setFilters: (filters: ProductFilters) => void;
  updateFilter: (key: keyof ProductFilters, value: any) => void;
  clearFilters: () => void;
  applyFilters: () => void;
}

const ProductFilterContext = createContext<ProductFilterContextType | undefined>(undefined);

export const useProductFilters = () => {
  const context = useContext(ProductFilterContext);
  if (!context) {
    throw new Error('useProductFilters must be used within a ProductFilterProvider');
  }
  return context;
};

interface ProductFilterProviderProps {
  children: ReactNode;
  onFiltersChange?: (filters: ProductFilters) => void;
}

export const ProductFilterProvider: React.FC<ProductFilterProviderProps> = ({ 
  children, 
  onFiltersChange 
}) => {
  const [filters, setFilters] = useState<ProductFilters>({
    categories: [],
    locations: [],
    priceRange: { min: 0, max: 10000 },
    brands: [],
    conditions: [],
    productTypes: []
  });

  const updateFilter = (key: keyof ProductFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      categories: [],
      locations: [],
      priceRange: { min: 0, max: 10000 },
      brands: [],
      conditions: [],
      productTypes: []
    });
  };

  const applyFilters = () => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  };

  return (
    <ProductFilterContext.Provider value={{
      filters,
      setFilters,
      updateFilter,
      clearFilters,
      applyFilters
    }}>
      {children}
    </ProductFilterContext.Provider>
  );
};

